import { isSupabaseConfigured, supabase } from '../lib/supabase.js'

const demoData = {
  links: [], steps: [], events: [], attributions: [], conversations: [], reports: [], stageHistory: [],
}

export async function loadGoogleAdsWorkspace(organizationId) {
  if (!isSupabaseConfigured) return structuredClone(demoData)
  const [links, steps, events, attributions, conversations, reports, stageHistory] = await Promise.all([
    supabase.from('google_ads_tracking_links').select('*').eq('organization_id', organizationId).order('created_at', { ascending: false }),
    supabase.from('google_ads_journey_steps').select('*').eq('organization_id', organizationId).eq('active', true).order('position'),
    supabase.from('google_ads_conversion_events').select('*').eq('organization_id', organizationId).eq('active', true).order('created_at'),
    supabase.from('google_ads_attributions').select('*, tracking_link:google_ads_tracking_links(id,name,slug), contact:whatsapp_contacts(id,name,phone_e164), conversation:whatsapp_conversations(id,last_message_preview,last_message_at), lead:leads(id,name,stage_id,estimated_value,won_value,closed_at)').eq('organization_id', organizationId).order('first_touch_at', { ascending: false }),
    supabase.from('whatsapp_conversations').select('id,contact_id,last_message_at,created_at,contact:whatsapp_contacts(id,name,phone_e164)').eq('organization_id', organizationId).order('last_message_at', { ascending: false }),
    supabase.from('google_ads_report_exports').select('*').eq('organization_id', organizationId).order('created_at', { ascending: false }).limit(50),
    supabase.from('lead_stage_history').select('id,lead_id,from_stage_id,to_stage_id,changed_at').eq('organization_id', organizationId).order('changed_at', { ascending: false }),
  ])
  const error = links.error || steps.error || events.error || attributions.error || conversations.error || reports.error || stageHistory.error
  if (error) throw error
  return { links: links.data || [], steps: steps.data || [], events: events.data || [], attributions: attributions.data || [], conversations: conversations.data || [], reports: reports.data || [], stageHistory: stageHistory.data || [] }
}

export async function loadTrackingDetails(conversationId) {
  if (!isSupabaseConfigured || !conversationId) return { initialMessage: '' }
  const { data, error } = await supabase
    .from('whatsapp_messages')
    .select('body,raw_payload,received_at,created_at')
    .eq('conversation_id', conversationId)
    .eq('direction', 'inbound')
    .order('received_at', { ascending: true })
    .limit(1)
    .maybeSingle()
  if (error) throw error
  return { initialMessage: data?.body || '', rawPayload: data?.raw_payload || null, receivedAt: data?.received_at || data?.created_at || null }
}

async function currentUser() {
  const { data, error } = await supabase.auth.getUser()
  if (error || !data.user) throw error || new Error('Sua sessão expirou.')
  return data.user
}

async function edgeFunctionError(error, data, fallback) {
  let message = data?.error
  if (!message && error?.context && typeof error.context.json === 'function') {
    try { message = (await error.context.clone().json())?.error } catch { /* resposta sem JSON */ }
  }
  return new Error(message || error?.message || fallback)
}

export async function saveTrackingLink(link, organizationId) {
  if (!isSupabaseConfigured) {
    const id = link.id || crypto.randomUUID()
    const code = id
    return { ...link, id, slug: code, destination_url: `https://route.caminhodavenda.com.br/w/${code}`, whatsapp_message: link.whatsappMessage, active: true, created_at: new Date().toISOString() }
  }
  const { data, error } = await supabase.functions.invoke('tracking-links-admin', { body: {
    action: link.id ? 'update' : 'create', organizationId, id: link.id || null,
    name: link.name, whatsappMessage: link.whatsappMessage,
  } })
  if (error) throw await edgeFunctionError(error, data, 'Não foi possível salvar o link rastreável.')
  if (!data?.link) throw new Error(data?.error || 'O Tracking não confirmou a criação do link.')
  return data.link
}

export async function archiveTrackingLink(id, organizationId) {
  if (!isSupabaseConfigured) return { id, active: false }
  const { data, error } = await supabase.functions.invoke('tracking-links-admin', { body: { action: 'archive', organizationId, id } })
  if (error) throw await edgeFunctionError(error, data, 'Não foi possível desativar o link rastreável.')
  if (!data?.link) throw new Error(data?.error || 'O Tracking não confirmou a desativação do link.')
  return data.link
}

export async function saveJourneyStep(step, organizationId) {
  if (!isSupabaseConfigured) return { ...step, id: step.id || crypto.randomUUID(), active: true }
  const user = await currentUser()
  const payload = { organization_id: organizationId, name: step.name.trim(), description: step.description?.trim() || null, position: Number(step.position) || 0, stage_id: step.stage_id || null, active: true }
  if (!step.id) payload.created_by = user.id
  const query = step.id ? supabase.from('google_ads_journey_steps').update(payload).eq('id', step.id).eq('organization_id', organizationId) : supabase.from('google_ads_journey_steps').insert(payload)
  const { data, error } = await query.select().single(); if (error) throw error; return data
}

export async function archiveJourneyStep(id, organizationId) {
  if (!isSupabaseConfigured) return { id, active: false }
  const { data, error } = await supabase.from('google_ads_journey_steps').update({ active: false }).eq('id', id).eq('organization_id', organizationId).select().single()
  if (error) throw error; return data
}

export async function saveConversionEvent(event, organizationId) {
  if (!isSupabaseConfigured) return { ...event, id: event.id || crypto.randomUUID(), active: true }
  const user = await currentUser()
  const payload = { organization_id: organizationId, platform: event.platform || 'google_ads', name: event.name.trim(), description: event.description?.trim() || null, event_key: event.event_key.trim().toLowerCase(), trigger_type: event.trigger_type, journey_step_id: event.journey_step_id || null, currency: event.currency || 'BRL', default_value: Number(event.default_value) || 0, active: true }
  if (!event.id) payload.created_by = user.id
  const query = event.id ? supabase.from('google_ads_conversion_events').update(payload).eq('id', event.id).eq('organization_id', organizationId) : supabase.from('google_ads_conversion_events').insert(payload)
  const { data, error } = await query.select().single(); if (error?.code === '23505') throw new Error('Já existe um evento com essa chave.'); if (error) throw error; return data
}

export async function archiveConversionEvent(id, organizationId) {
  if (!isSupabaseConfigured) return { id, active: false }
  const { data, error } = await supabase.from('google_ads_conversion_events').update({ active: false }).eq('id', id).eq('organization_id', organizationId).select().single()
  if (error) throw error; return data
}

const csvCell = value => `"${String(value ?? '').replaceAll('"', '""')}"`
const googleDate = value => {
  const date = new Date(value)
  const parts = new Intl.DateTimeFormat('sv-SE', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).format(date)
  return `${parts}-03:00`
}

export function buildOfflineConversionsCsv(attributions, conversionEvent) {
  const rows = attributions.filter(item => (item.gclid || item.gbraid || item.wbraid) && item.lead?.closed_at && Number(item.lead?.won_value) > 0)
  const header = ['Google Click ID', 'Conversion Name', 'Conversion Time', 'Conversion Value', 'Conversion Currency']
  const lines = rows.map(item => [item.gclid || item.gbraid || item.wbraid, conversionEvent.name, googleDate(item.lead.closed_at), Number(item.lead.won_value).toFixed(2), conversionEvent.currency || 'BRL'])
  return { count: rows.length, content: [header, ...lines].map(row => row.map(csvCell).join(',')).join('\r\n') }
}

export function downloadCsv(content, fileName) {
  const blob = new Blob(['\ufeff', content], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a'); anchor.href = url; anchor.download = fileName; anchor.click()
  URL.revokeObjectURL(url)
}

export async function saveReportExport(report, organizationId) {
  if (!isSupabaseConfigured) return { ...report, id: crypto.randomUUID(), status: 'processed', created_at: new Date().toISOString() }
  const user = await currentUser()
  const { data, error } = await supabase.from('google_ads_report_exports').insert({ organization_id: organizationId, report_type: report.report_type, status: 'processed', row_count: report.row_count, file_name: report.file_name, content: report.content, created_by: user.id }).select().single()
  if (error) throw error
  return data
}
